// The "more" control at the end of the chart picker: a button that opens a listbox of the symbols without a
// button of their own. Replaces a native <select>, whose system popup ignored the page's colors.
// Keyboard: Enter, Space or the arrows open; arrows, Home, End, PageUp and PageDown move; Enter picks; Escape
// and Tab close. Focus returns to the button. A filter input sits above the list from FILTER_FROM symbols on.
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { CLOSED, FILTER_FROM, filterMore, moreKey, openMore, setQuery, type MoreItem, type MoreState } from "./more";

export interface MorePickerProps {
  items: MoreItem[];
  /** The current symbol when it is in the list, else null. */
  selected: string | null;
  onPick: (sym: string) => void;
}

/** Panel width; the panel opens toward the left when there is not this much room right of the button. */
const PANEL_W = 300;
const optId = (sym: string) => `chart-more-opt-${sym}`;

export function MorePicker({ items, selected, onPick }: MorePickerProps) {
  const [st, setSt] = useState<MoreState>(CLOSED);
  const [alignEnd, setAlignEnd] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const withFilter = items.length > FILTER_FROM;
  const shown = filterMore(items, st.query);
  const activeSym = st.open ? shown[st.active]?.sym : undefined;

  const open = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    setAlignEnd(r ? r.left + PANEL_W > window.innerWidth - 16 : false);
    setSt(openMore(items, selected));
  };
  const close = (focusTrigger: boolean) => {
    setSt(CLOSED);
    if (focusTrigger) triggerRef.current?.focus();
  };
  const pick = (sym: string) => {
    close(true);
    if (sym !== selected) onPick(sym);
  };

  // Focus the filter (or the list) when the panel opens.
  useEffect(() => {
    if (!st.open) return;
    (withFilter ? inputRef.current : listRef.current)?.focus({ preventScroll: true });
  }, [st.open, withFilter]);

  // Keep the highlighted row in view.
  useEffect(() => {
    if (!activeSym) return;
    document.getElementById(optId(activeSym))?.scrollIntoView({ block: "nearest" });
  }, [activeSym]);

  // A press anywhere outside closes the panel without moving focus.
  useEffect(() => {
    if (!st.open) return;
    const onDown = (ev: PointerEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setSt(CLOSED);
    };
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("pointerdown", onDown);
    };
  }, [st.open]);

  const onTriggerKey = (ev: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (st.open || (ev.key !== "ArrowDown" && ev.key !== "ArrowUp")) return;
    ev.preventDefault();
    open();
  };
  const onPanelKey = (space: boolean) => (ev: ReactKeyboardEvent) => {
    const r = moreKey(st, ev.key, items, space);
    if (!r.handled) return;
    ev.preventDefault();
    setSt(r.state);
    if (r.focusTrigger) triggerRef.current?.focus();
    if (r.pick && r.pick !== selected) onPick(r.pick);
  };

  const label = selected ?? "more";
  return (
    <div className={st.open ? "more is-open" : "more"} ref={rootRef}>
      <button
        type="button" id="chart-more" ref={triggerRef}
        className={selected ? "cpick more-trigger is-on" : "cpick more-trigger"}
        aria-haspopup="listbox" aria-expanded={st.open} aria-controls="chart-more-panel"
        aria-label={selected ? `More Stock Tokens: ${selected}` : "More Stock Tokens"}
        onClick={() => { if (st.open) close(false); else open(); }}
        onKeyDown={onTriggerKey}
      >
        <span className="more-label">{label}</span>
        <svg className="more-chev" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true" focusable="false">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>
      <div className={alignEnd ? "more-panel is-end" : "more-panel"} id="chart-more-panel" hidden={!st.open}>
        {withFilter && (
          <input
            ref={inputRef} className="more-filter" type="text" inputMode="search" autoComplete="off" spellCheck={false}
            placeholder="Filter by symbol or name" aria-label="Filter Stock Tokens"
            role="combobox" aria-expanded={st.open} aria-controls="chart-more-list" aria-autocomplete="list"
            aria-activedescendant={activeSym ? optId(activeSym) : undefined}
            value={st.query}
            onChange={(ev) => { setSt(setQuery(items, ev.target.value)); }}
            onKeyDown={onPanelKey(false)}
          />
        )}
        <ul
          ref={listRef} className="more-list" id="chart-more-list" role="listbox" aria-label="More Stock Tokens"
          tabIndex={withFilter ? undefined : -1}
          aria-activedescendant={!withFilter && activeSym ? optId(activeSym) : undefined}
          onKeyDown={withFilter ? undefined : onPanelKey(true)}
        >
          {shown.map((it, i) => (
            <li
              key={it.sym} id={optId(it.sym)} role="option" aria-selected={it.sym === selected}
              className={i === st.active ? "more-opt is-active" : "more-opt"} data-sym={it.sym}
              onPointerMove={() => { if (i !== st.active) setSt({ ...st, active: i }); }}
              onClick={() => { pick(it.sym); }}
            >
              <span className="more-sym">{it.sym}</span>
              <span className="more-name">{it.name}</span>
            </li>
          ))}
        </ul>
        {shown.length === 0 && <p className="more-empty">No symbol matches “{st.query.trim()}”.</p>}
      </div>
    </div>
  );
}
